import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  People as UsersIcon,
  DesktopWindows as SessionsIcon,
  DataUsage as UsageIcon,
} from '@mui/icons-material';
import api from '../../api/axios';
import { ClientDashboardStats } from '../../types';
import { useTranslation } from 'react-i18next';

const ClientDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<ClientDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get<ClientDashboardStats>('/client/dashboard');
      setStats(response.data);
    } catch (err: any) {
      const message = err.response?.data?.message || t('common.error');
      setError(message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  const usagePercent = stats ? Math.round((stats.totalUsers / stats.maxUsers) * 100) : 0;
  const usageColor =
    usagePercent >= 90 ? '#e53935' : usagePercent >= 70 ? '#ff9800' : '#4caf50';

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
          {t('client.dashboard.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: '#78909c' }}>
          {t('client.dashboard.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)' },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#78909c', fontWeight: 500, mb: 0.5 }}>
                    {t('client.dashboard.totalUsers')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#263238' }}>
                    {stats?.totalUsers ?? 0}
                    <Typography
                      component="span"
                      variant="h5"
                      sx={{ fontWeight: 400, color: '#90a4ae', ml: 0.5 }}
                    >
                      / {stats?.maxUsers ?? 0}
                    </Typography>
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e3f2fd',
                  }}
                >
                  <UsersIcon sx={{ fontSize: 32, color: '#1565c0' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)' },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#78909c', fontWeight: 500, mb: 0.5 }}>
                    {t('client.dashboard.activeSessions')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#263238' }}>
                    {stats?.activeSessions ?? 0}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e8f5e9',
                  }}
                >
                  <SessionsIcon sx={{ fontSize: 32, color: '#2e7d32' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'translateY(-4px)' },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#78909c', fontWeight: 500, mb: 0.5 }}>
                    {t('client.dashboard.userUsage')}
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#263238' }}>
                    {usagePercent}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff3e0',
                  }}
                >
                  <UsageIcon sx={{ fontSize: 32, color: '#e65100' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card
        sx={{
          mt: 4,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#37474f', mb: 2 }}>
            {t('client.dashboard.userCapacity')}
          </Typography>
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: '#78909c' }}>
              {t('client.dashboard.usersOf', { current: stats?.totalUsers ?? 0, max: stats?.maxUsers ?? 0 })}
            </Typography>
            <Typography variant="body2" sx={{ color: usageColor, fontWeight: 600 }}>
              {usagePercent}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={usagePercent}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
                backgroundColor: usageColor,
              },
            }}
          />
          {usagePercent >= 90 && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              {t('client.dashboard.approachingLimit')}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          variant="filled"
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientDashboard;
